import React, { useState } from 'react';
import { Mail, MapPin, Clock, Send, Loader2 } from 'lucide-react';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { app } from '../firebase';
import SEO from '../components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
    projectType: '',
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize Firestore
  const db = getFirestore(app);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      // Add the message to Firestore
      await addDoc(collection(db, 'contact_messages'), {
        ...formData,
        createdAt: serverTimestamp(),
        status: 'new'
      });

      // Clear the form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        message: '',
        projectType: '',
      });

      // Show success message
      setFormStatus({
        type: 'success',
        message: 'Thank you for your message. We will get back to you soon!'
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus({
        type: 'error',
        message: 'There was an error sending your message. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const inputClasses = "w-full bg-[#1E293B]/50 border border-white/[0.06] rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition";

  return (
    <>
      <SEO
        title="Contact"
        description="Get in touch with P2P Digital for medical device consulting, AI solutions, and digital health projects. Based in Perth, serving clients globally."
        path="/contact"
      />
      <div>
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 overflow-hidden">
          {/* Glow orbs */}
          <div className="absolute top-20 left-1/4 w-72 h-72 bg-cyan-500/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px]" />

          <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4">
              GET IN TOUCH
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white">
              Let's Build Something <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                Extraordinary
              </span>
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto mt-6">
              Ready to transform your ideas into reality? Reach out and let's discuss how we can bring your vision to life.
            </p>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="py-8 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Email */}
            <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 text-center hover:-translate-y-1 transition-all duration-300">
              <Mail className="w-10 h-10 mx-auto text-cyan-400 mb-4" />
              <h3 className="text-white font-display font-semibold mb-2">Email</h3>
              <a
                href="mailto:contact@p2pdigital.com.au"
                className="text-cyan-400 text-sm hover:text-cyan-300 transition-colors"
              >
                contact@p2pdigital.com.au
              </a>
            </div>

            {/* Location */}
            <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 text-center hover:-translate-y-1 transition-all duration-300">
              <MapPin className="w-10 h-10 mx-auto text-cyan-400 mb-4" />
              <h3 className="text-white font-display font-semibold mb-2">Location</h3>
              <p className="text-slate-400 text-sm">Perth, Western Australia</p>
            </div>

            {/* Response Time */}
            <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 text-center hover:-translate-y-1 transition-all duration-300">
              <Clock className="w-10 h-10 mx-auto text-cyan-400 mb-4" />
              <h3 className="text-white font-display font-semibold mb-2">Response Time</h3>
              <p className="text-slate-400 text-sm">Within 24 hours</p>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-16 max-w-3xl mx-auto px-6">
          <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 lg:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* First / Last Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block font-mono text-xs uppercase tracking-wider text-slate-400 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder="John"
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block font-mono text-xs uppercase tracking-wider text-slate-400 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    placeholder="Doe"
                    className={inputClasses}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block font-mono text-xs uppercase tracking-wider text-slate-400 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                  className={inputClasses}
                />
              </div>

              {/* Project Type */}
              <div>
                <label htmlFor="projectType" className="block font-mono text-xs uppercase tracking-wider text-slate-400 mb-2">
                  Project Type
                </label>
                <select
                  name="projectType"
                  id="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                >
                  <option value="">Select a project type</option>
                  <option value="Medical Device Consulting">Medical Device Consulting</option>
                  <option value="Software Development">Software Development</option>
                  <option value="AI Integration & Consultancy">AI Integration &amp; Consultancy</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block font-mono text-xs uppercase tracking-wider text-slate-400 mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Tell us about your project..."
                  className={inputClasses}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 rounded-xl font-display font-semibold text-white text-sm transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)',
                  boxShadow: '0 0 20px rgba(6, 182, 212, 0.3), 0 0 40px rgba(6, 182, 212, 0.1)',
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </button>

              {/* Status Messages */}
              {formStatus.type === 'success' && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl p-4 mt-6">
                  {formStatus.message}
                </div>
              )}
              {formStatus.type === 'error' && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl p-4 mt-6">
                  {formStatus.message}
                </div>
              )}
            </form>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
